import { BaseResponseType } from '../cognito/authResponse.types';
import { VisibilityOptionsType } from '../menu/item.types';
import { EmployeePayTypeEnum } from '../user-roles/user-roles.types';
import { CashDrawerType } from './cashDrawerLogs.types';
import { IOwnerInfo, PocDetail } from '../business/business.types';
import { IBrandBook } from '../brandBook/content.type';
import { RecordStatus } from '../menu/enums.types';
import { RestaurantTimingDataType } from './restaurantTiming.types';
import { DomainSettings } from '../builder/builder.types';
import { OnlinePaymentSource } from '../payment/payment.types';
import { createMappings } from '../../utils/utils';
import { BreaksSetting, SuggestedTip } from '../restaurant-settings/generalSettings.types';

export interface RestaurantResponseType {
  id: number;
  name: string;
  restaurantId?: number;
  address: string;
  image?: string;
  onlineOrder: boolean;
  autoFire: boolean;
  autoFireTimer: string;
  onlineOrderTimer: string;
  latitude: number;
  longitude: number;
  createdAt: Date;
  updatedAt: Date;
  accountId?: string;
  restaurant?: IRestaurant;
  isDefault?: boolean;
  status?: RestaurantStatus;
  kitchenHubLocationId?: string;
  // NEED TO ADD SCHEMA
  kitchenHubInfo?: {
    partnerName: string;
    integrationAccountId: string;
    storeId?: string;
    deliveryEnabled?: boolean;
    testMode?: boolean;
    autoCancelTimeInMinutes?: number;
    deliveryRadius?: number;
  }[];
}

export interface ActiveRestaurantData {
  id: number;
  name: string;
  address: string;
  image: string;
  restaurantAlias: string;
  business: {
    id: number;
    businessName: string;
    businessAlias: string;
  };
}

export interface RestaurantRequestType {
  id?: number;
  name: string;
  address: string;
  onlineOrder: boolean;
  autoFire: boolean;
  autoFireTimer: string;
  onlineOrderTimer: string;
  latitude?: number;
  longitude?: number;
}

export interface SingleRestaurantDataType {
  restaurant: RestaurantResponseType;
  totalReviews: number;
  averageRating: number;
}

export enum RestaurantMode {
  QuickServe = 'quickServe',
  CasualDining = 'casualDining',
  FineDining = 'fineDining',
  EthnicRestaurants = 'ethnicRestaurants',
  CafésAndBistros = 'cafésAndBistros',
  Steakhouses = 'steakhouses',
  SeafoodRestaurants = 'seafoodRestaurants',
  Diners = 'diners',
  BuffetRestaurants = 'buffetRestaurants',
  FoodTrucksAndFoodCarts = 'foodTrucksAndFoodCarts',
  Pubs = 'pubs',
  Bars = 'bars',
  Other = 'other',
}

export const RestaurantIndustryCode: Record<RestaurantMode, string> = {
  ...createMappings([RestaurantMode.FineDining, RestaurantMode.Steakhouses, RestaurantMode.Diners], '722511'), // Full-service restaurants
  ...createMappings(
    [
      RestaurantMode.QuickServe,
      RestaurantMode.CasualDining,
      RestaurantMode.EthnicRestaurants,
      RestaurantMode.SeafoodRestaurants,
      RestaurantMode.Pubs,
      RestaurantMode.Bars,
    ],
    '722513'
  ), // Limited-service restaurants
  ...createMappings([RestaurantMode.CafésAndBistros, RestaurantMode.BuffetRestaurants], '722514'), // Cafeterias, grill buffets, and buffets
  [RestaurantMode.FoodTrucksAndFoodCarts]: '7223', // Special Food Service
  [RestaurantMode.Other]: '722515', // Snacks and nonalcoholic beverage bars
};

export enum TimeZone {
  AmericaNew_York = 'America/New_York',
  AmericaLos_Angeles = 'America/Los_Angeles',
  AmericaChicago = 'America/Chicago',
  AmericaPhoenix = 'America/Phoenix',
  AmericaDenver = 'America/Denver',
  AmericaAnchorage = 'America/Anchorage',
  PacificHonolulu = 'Pacific/Honolulu',
}

export enum RestaurantStatus {
  OPEN = 'open',
  CLOSED = 'closed',
}

export enum RestaurantphoneNoFormat {
  US = 'US',
  PK = 'PK',
}

export enum RestaurantLiveStatus {
  LIVE = 'live',
  DEMO = 'demo',
}

export enum AmountDeclarationType {
  CASH = 'cash',
  TIPs = 'tips',
}

export interface posOnboardRequestType {
  id?: number;
  name?: string;
  ipAddress?: string;
  primaryScreen?: string;
  connectionStatus?: boolean;
  config?: Record<string, any>;
  cashDrawer?: CashDrawerType;
  printers?: Printer[];
  paymentTerminal?: PaymentTerminal;
  assigneeId?: string;
}

export interface deviceOnboardRequestType {
  serialNumberHash?: string;
  encryptIdentificationNumber: string;
}

export interface POSDataResponse {
  id?: number;
  name?: string;
  ipAddress?: string;
  primaryScreen?: string;
  connectionStatus?: boolean;
  config?: Record<string, any>;
  cashDrawer?: CashDrawerType;
  printers?: Printer[];
  paymentTerminal?: PaymentTerminal;
  restaurantId?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Printer {
  id: number;
  name: string;
  ipAddress: string;
  serialNumber: string;
  connectionStatus: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Station {
  id?: number;
  name?: string;
  stationType?: string;
  image?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PaymentTerminal {
  id: number;
  name: string;
  serialNumber: string;
  connectionStatus: boolean;
  config?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface posOnboardResponseType {
  success: boolean;
  data: POSDataResponse;
  message: string;
}

export interface printerResponseType {
  success: boolean;
  data: Printer[];
  message: string;
}

export interface paymentTerminalResponseType {
  success: boolean;
  data: PaymentTerminal[];
  message: string;
}

export interface kdsOnboardRequestType {
  id?: number;
  name?: string;
  ipAddress?: string;
  kdsIdentifier?: string;
  connectionStatus?: boolean;
  serialNumber?: string;
  displayAvgFulfillmentTime?: boolean;
  doubleTabFullfill?: boolean;
  stations?: Station[];
}

export interface RestaurantOrderoutType {
  id: number;
  businessId?: number;
  orderoutRestaurantId: number;
  orderoutMenuId: number;
  restaurantTimings: RestaurantTimingDataType[];
}

export interface KDSDataResponse {
  id?: number;
  name: string;
  ipAddress: string;
  kdsIdentifier: string;
  connectionStatus: boolean;
  stations?: Station[];
  displayAvgFulfillmentTime?: boolean;
  doubleTabFullfill?: boolean;
  restaurantId?: number;
  createdAt: Date;
  updatedAt: Date;
}
export interface kdsOnboardResponseType {
  success: boolean;
  data: KDSDataResponse;
  message: string;
}

export interface KIOSKDataResponse {
  id?: number;
  name?: string;
  ipAddress?: string;
  identificationNumber?: string;
  connectionStatus: boolean;
  restaurant?: RestaurantResponseType;
  printReceipts?: boolean;
  dineIn?: boolean;
  takeOut?: boolean;
  specialRequests?: boolean;
  screenTimeout?: boolean;
  screenTimeoutTimer?: string | object;
  itemsTimeout?: boolean;
  itemsTimeoutTimer?: string | object;
  categoryTimeout?: boolean;
  categoryTimeoutTimer?: string | object;
  createdAt: Date;
  updatedAt: Date;
}

export enum DeviceType {
  POS = 'pos',
  KDS = 'kds',
  Printer = 'printer',
  PaymentTerminal = 'payment-terminal',
  CashDrawer = 'cash-drawer',
  KIOSK = 'kiosk',
  MPOS = 'mpos',
  KDC = 'kdc',
  TV = 'tv',
  Nugget = 'nugget',
  KDP = 'kdp',
  STATION = 'station',
  StickyPrinter = 'sticky-printer',
}

export enum DeviceOutputType {
  POS = 'POS',
  KDS = 'KDS',
  Printer = 'PRINTER',
  stickyPrinter = 'STICKY-PRINTER',
  PaymentTerminal = 'payment-terminal',
  CashDrawer = 'cash-drawer',
  KIOSK = 'KIOSK',
  MPOS = 'MPOS',
  KDC = 'KDC',
  TV = 'TV',
  Nugget = 'NUGGET',
  KDP = 'KDP',
  STATION = 'STATION',
}

export enum KdsServerLogStatus {
  PENDING = 'pending',
  success = 'success',
  FAILED = 'failed',
}

export interface assignStationPrinterRequestType {
  stationIds?: number[];
}
export interface assignStationPrinterResponseType {
  success: boolean;
  data: Station;
  message: string;
}

export interface editPrinterRequestType {
  stationId?: number;
  stationStatus?: boolean;
  posId?: number;
  postStatus?: boolean;
}

export interface editPrinterResponseType {
  success: boolean;
  data: Station;
  message: string;
}

export interface MomMyRestaurantType {
  id: number;
  name: string;
  address: string;
  image: string;
  onlineOrder: boolean;
  restaurantModes: string;
  phoneNo: string;
  building: string;
  city: string;
  state: string;
  zipCode: string;
  status: string;
  cuisineType: string;
  business: {
    businessName: string;
  };
  otherMode?: string;
}
export interface MomMyRestaurantDataType {
  restaurant: MomMyRestaurantType | null;
}
export interface MomRestaurantResponseTypes extends BaseResponseType {
  data: MomMyRestaurantDataType | null;
}

export interface kioskOnboardRequestType {
  id?: number;
  name?: string;
  ipAddress?: string;
  identificationNumber?: string;
  connectionStatus?: boolean;
  // serialNumber?: string;
  // displayAvgFulfillmentTime?: boolean;
  // doubleTabFullfill?: boolean;
  // stations?: Station[];
}

export interface nuggetOnboardRequestType {
  id?: number;
  name?: string;
  ipAddress?: string;
  serialNumber?: string;
  tableId?: number;
  restaurantId?: number;
  // connectionStatus?: boolean;
}

export interface KdsServerCallType {
  serverId: string;
  ticketId: number;
}
export interface KdsServerCallSuccessLogType {
  kdsServerLogId: number;
}
export interface KdsServerCallSuccessLogType {
  kdsServerLogId: number;
}
export interface kioskOnboardResponseType {
  success: boolean;
  data: KIOSKDataResponse;
  message: string;
}

export interface nuggetOnboardResponseType {
  success: boolean;
  data: NuggetDataResponse;
  message: string;
}

export interface onboardV2ResponseType {
  success: boolean;
  data: onboardV2DataResponse;
  message: string;
}

export interface KdsServerCallResponseType {
  success: boolean;
  message: string;
  error?: string | null;
}
export interface KIOSKDataResponse {
  id?: number;
  name?: string;
  ipAddress?: string;
  identificationNumber?: string;
  connectionStatus: boolean;
  // stations?: Station[];
  // displayAvgFulfillmentTime?: boolean;
  // doubleTabFullfill?: boolean;
  restaurantId?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface NuggetDataResponse {
  id?: number;
  name?: string;
  ipAddress?: string;
  serialNumber?: string;
  connectionStatus?: boolean;
  restaurantId?: number;
}

export interface onboardV2DataResponse {
  restaurant: IRestaurant;
}

export interface KdcResponse {
  id: number;
  identificationNumber: string;
  name: string;
  ipAddress: string;
  connectionStatus: boolean;
  readyTabTimer: string;
  screenTimeoutTimer: string;
  screenTimeout: boolean;
  restaurantId?: number;
  createdAt: Date;
  updatedAt: Date;
}

//!------------------DASHBOARD---------------------//

export interface IRestaurant {
  id?: number;
  businessId?: number;
  name: string;
  address: string;
  image?: string;
  orderDelayedTime?: number;
  onlineOrder?: boolean;
  restaurantModes: RestaurantMode;
  autoFire?: boolean;
  autoFireTimer?: string; //'00:00:00'::interval
  onlineOrderTimer?: string; //'00:00:00'::interval
  latitude?: number;
  longitude?: number;
  phoneNo: string;
  restaurantAlias?: string;
  otherMode?: string;
  building: string;
  city?: string;
  state?: string;
  zipCode: string;
  status: RestaurantStatus;
  isPickupEnabled?: boolean;
  isDeliveryEnabled?: boolean;
  isCateringEnabled?: boolean;
  autoGratuity?: boolean;
  largePartySizeLimit?: number;
  cuisineType?: string;
  timezone?: string;
  createdAt?: Date;
  deletedAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
  deletedBy?: string;
  businessName?: string;
  workplaceId?: string;
  stateIso?: string;
  closeOutTime?: string;
  accountId?: string;
  momTopicArn?: string;
  mqttTopic?: string;
  domainSettings?: DomainSettings;
  brandBook?: IBrandBook;
  stateId?: number;
  business?: {
    id: number;
    businessName: string;
    businessSince: Date;
    taxId: string;
    businessType: string;
    address: string;
    suite?: string;
    zipCode: string;
    contactNo: string;
    city: string;
    state: string;
    FEIN?: string;
    logo?: string;
    ownerInfo?: IOwnerInfo;
    companyId?: string;
    createdAt: Date;
    updatedAt: Date;
    status: RecordStatus;
    deletedAt?: Date;
    createdBy?: string;
    updatedBy?: string;
    deletedBy?: string;
    businessAlias?: string;
  };
}

export interface ILocationData {
  location: IRestaurant;
  pocInfo: PocDetail[];
  settingsToggles: ISettingsToggles;
}

export interface IRestaurantListResponse {
  restaurants: IRestaurant[] | undefined;
  totalCount: number;
}

export interface IRestaurantRequest {
  businessId: number;
  id?: number;
  restaurantId?: number;
  companyId?: string;
  restaurant: IRestaurant;
  pocInfo: PocDetail[];
}

export interface IRestaurantDevice {
  device: string;
  ipAddress: string;
  serialNo: string;
}

/** Policy row + nested policy for employee list (time off assignments). */
export interface IEmployeePolicyUserRestaurant {
  id: number;
  policyId: number;
  userRestaurantsId: number;
  leavesTaken: number;
  waitingPeriod?: number | null;
  carryOverLimit?: number | null;
  carriedOver?: number | null;
  timeoffCycleId?: number | null;
  policy?: IEmployeePolicySummary | null;
}

export interface IEmployeePolicySummary {
  id: number;
  name: string;
  policyType: string;
  accrualMethod?: string | null;
  hours?: number | null;
  hoursWorked?: number | null;
  waitingPeriod?: number | null;
  carryOverLimit?: number | null;
  maxAccuralPerYear: number;
  maxUsagePerYear?: number | null;
  autoAssign?: boolean | null;
  status: string;
  restaurantId: number;
}

export interface IRestaurantEmployee {
  id?: string;
  firstName: string;
  fullName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  status?: string;
  userRole: IEmployeeRoles[];
  employeeDisplayId?: number;
  posPin?: number;
  userRestaurantId?: number;
  policyUserRestaurants?: IEmployeePolicyUserRestaurant[];
}

export interface IEmployeeRoles {
  roleId: number | null;
  payType: EmployeePayTypeEnum;
  salary: number;
  roleName?: string;
  tag?: string | unknown;
}

export interface ICreateUpdateEmployeeResponse {
  data?: object;
  error?: string;
  message?: string;
  success?: boolean;
}

export interface IRestaurantFormData {
  devices: string[];
}

export interface ISettingsToggles {
  isTaxInclusive: boolean;
  isSecondaryScreen: boolean;
  isLoyalty: boolean;
  applyScTaxesBeforeDeals: boolean;
  isBohKitchenReceiptsEnabled: boolean;
  isThirdPartyOnboarded: boolean;
  isOnlineOrderingEnabled: boolean;
  breaksSetting: BreaksSetting.COMPLIANT | BreaksSetting.NO_BREAKS | BreaksSetting.NON_COMPLIANT;
  isAdyenOnboarded?: boolean;
  giftCardsEnabled: boolean;
  dealsEnabled: boolean;
}

export type RestaurantFilter = Partial<IRestaurant>;

export interface Devices {
  id: number;
  name: string;
  serialNo: string;
  ipAddress: string;
  status: string;
  printer?: string;
  printerId?: string | number;
  stickyPrinterId?: string | number;
  stickyPrinterName?: string;
  stationIds?: string[];
  tableId?: number | null;
  table?: string;
  stations: string[];
  orientation?: string;
  paymentSource?: OnlinePaymentSource;
  paymentDeviceSerial?: string;
}

export interface DeviceList {
  name: string;
  data: Devices[];
}

export interface ResultItem {
  deviceType: DeviceType;
  data: Devices[];
}

export interface DeviceListData {
  restaurants?: DeviceList[];
}

export interface PrinterData {
  printers: Printer[];
  stickyPrinters: Printer[];
  printerIps: string[];
  existingSerialNumber: [{ deviceSerialNumber: string; restaurantId: number | null }] | [];
  existingIps: [{ ipAddress: string; restaurantId: number | null }] | [];
  stations: Printer[];
  tables: TablesList[];
}

interface TablesList {
  value: number | null;
  label: string;
}

export interface DeleteDeviceData {
  id: number;
  deviceType: string;
}

export interface SelectedStateCity {
  value: string;
}

export interface PrinterStation {
  id: string;
  name: string;
  type: string;
}

export interface Table {
  id: string;
  tableNo: string;
}
export interface DeviceII {
  id: string;
  name: string;
  identificationNumber?: string;
  kdsIdentifier?: string;
  serialNumber?: string;
  ipAddress: string;
  connectionStatus: boolean;
  printers?: PrinterStation[];
  stations?: PrinterStation[];
  tables?: Table[];
  orientation?: string;
  paymentSource?: OnlinePaymentSource;
  paymentDeviceSerial?: string;
}

export interface TransformedDevice {
  id: string;
  name: string;
  serialNo: string;
  ipAddress: string;
  status: string;
  printer: string;
  stations: string[];
  printerId: string;
  stationIds: string[];
  table: string;
  tableId: string;
}

export interface ApiData {
  [deviceType: string]: DeviceII[];
}

export interface Product {
  name: string;
}
export interface OnlineRestaurantRequestType extends VisibilityOptionsType {
  restaurantId: number;
  userId?: string;
  menuId?: number;
}

export interface AdyenAccountDetailsType {
  balanceAccountId: string;
  accountHolderId: string;
  legalEntityId: string;
  onboardingErrors: Record<string, any>;
  sweepId: string;
  storeId: string;
  businessLineId: string;
  storeName: string;
  webhookProcessingErrors: WebhookProcessingErrors;
}

export interface WebhookProcessingErrors {
  sweep: {
    isCompleted: boolean;
    errors: string;
  };
  store: {
    isCompleted: boolean;
    errors: string;
  };
  payment_Methods: {
    isCompleted: boolean;
    errors: string;
  };
}

export interface RestaurantCustomer {
  id: number;
  customerId: string;
  restaurantId: number;
  userPoints: number;
}

export interface eCardCredInfo {
  username: string | null;
  password: string | null;
  endpoint: string | null;
}
export interface eCardCredType {
  gateway: eCardCredInfo;
  digital: eCardCredInfo;
}

export interface RestaurantDynamicBannerImages {
  type: 'deal' | 'giftcard' | 'loyalty' | 'menu-item';
  dealId?: number | null;
  bannerUrl: string;
  dealStartTime?: string | null;
  dealEndTime?: string | null;
  dealStartDate?: string | null;
  dealEndDate?: string | null;
}

export enum PrinterType {
  Printer = 'printer',
  StickyPrinter = 'sticky-printer',
}

export interface TipsState {
  suggestedTips: SuggestedTip[];
  customTipEnabled: boolean;
}
